/**
 * Authentication Service
 *
 * This service provides authentication-related API calls.
 *
 * With Logto integration:
 * - Primary authentication flow uses Logto SDK (signIn, signOut from @logto/react)
 * - This service is used for backend sync and legacy support
 * - The login/register functions are deprecated but kept for migration period
 */
import axios from 'axios';
import apiClient, { markLoginTime } from './apiClient';
import i18n from '../i18n/config';
import { UserCreate } from '../types';

const apiBaseURL = '/auth';
const usersBaseURL = '/users';

// Type definitions
type User = {
  id: string;
  email: string;
  full_name?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string;
  is_active?: boolean;
  organization_id?: string | null;
  organization_is_active?: boolean | null;
  avatar_url?: string | null;
  preferences?: Record<string, any> | null;
};

const authService = {
  markLoginTime: (): void => {
    markLoginTime();
  },

  isAccountDeletedError: (error: unknown): boolean => {
    return (
      axios.isAxiosError(error) &&
      error.response?.status === 403 &&
      error.response?.data?.detail === 'account_deleted'
    );
  },

  /**
   * Sync user with backend after Logto authentication.
   * Called automatically during the callback flow.
   *
   * @param accessToken - The access token from Logto
   * @returns The user data from our backend
   */
  syncUserWithBackend: async (accessToken: string): Promise<User> => {
    try {
      const response = await apiClient.get<User>(`${apiBaseURL}/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      console.log('[authService] /auth/me response:', response.data);
      console.log('[authService] profile_id:', (response.data as any).profile_id, 'profile:', (response.data as any).profile);
      return response.data;
    } catch (error: any) {
      console.error("Failed to sync user with backend:", error);
      const errorMessage = error.response?.data?.detail || error.message || i18n.t('errors.auth.syncUser');
      throw new Error(errorMessage);
    }
  },

  /**
   * Get current user profile from backend.
   *
   * @returns The current user's profile
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await apiClient.get<User>(`${usersBaseURL}/me`);
      return response.data;
    } catch (error: any) {
      console.error("Failed to get current user:", error);
      const errorMessage = error.response?.data?.detail || error.message || i18n.t('errors.auth.getProfile');
      throw new Error(errorMessage);
    }
  },

  /**
   * [DEPRECATED] Legacy login with email/password.
   * Use Logto signIn() instead.
   *
   * @deprecated Use Logto SDK signIn() for authentication
   */
  login: async (credentials: { username: string; password: string }): Promise<{ tokens: { access_token: string; refresh_token: string }, user: User }> => {
    console.warn('authService.login() is deprecated. Use Logto signIn() instead.');
    try {
      const loginResponse = await apiClient.post<{ access_token: string; refresh_token: string }>(`${apiBaseURL}/login`, credentials);
      const tokens = {
        access_token: loginResponse.data.access_token,
        refresh_token: loginResponse.data.refresh_token,
      };

      const userProfileResponse = await apiClient.get<User>(`${usersBaseURL}/me`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });
      const user = userProfileResponse.data;

      return { tokens, user };
    } catch (error: any) {
      console.error("Login service failed:", error);
      const errorMessage = error.response?.data?.detail || error.message || i18n.t('errors.auth.login');
      throw new Error(errorMessage);
    }
  },

  /**
   * [DEPRECATED] Legacy logout.
   * Use Logto signOut() instead.
   *
   * @deprecated Use Logto SDK signOut() for logout
   */
  logout: async (): Promise<void> => {
    console.warn('authService.logout() is deprecated. Use Logto signOut() instead.');
    // No backend logout endpoint needed with Logto
    // Logto handles session invalidation
  },

  /**
   * [DEPRECATED] Legacy registration with email/password.
   * Use Logto signIn() - Logto handles registration.
   *
   * @deprecated Use Logto for user registration
   */
  register: async (userData: UserCreate): Promise<User> => {
    console.warn('authService.register() is deprecated. Use Logto for registration.');
    try {
      const response = await apiClient.post<User>(`${apiBaseURL}/register`, userData);
      return response.data;
    } catch (error: any) {
      console.error("Register service failed:", error);
      const errorMessage = error.response?.data?.detail || error.message || i18n.t('errors.auth.register');
      throw new Error(errorMessage);
    }
  },
};

export default authService;
